import React, { useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '../../styles/ThemeProvider';
import {
  ExportButton, KpiCard, ChartCard, DataTable, EmptyState, TrendLine,
} from '../../components/analytics';
import { formatTRY, formatPct, formatCompact } from '../../utils/analytics/format';
import { exportPdf } from '../../utils/analytics/export';
import { useAnalyticsQuery } from '../../hooks/useAnalyticsQuery';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const authHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const tryFetch = async (url, signal) => {
  try {
    const res = await fetch(url, { signal, headers: authHeaders() });
    if (!res.ok) return null;
    return await res.json();
  } catch { return null; }
};

export default function TedarikciDetayPage() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();
  const containerRef = useRef(null);

  const { data: tedarikci, loading: loadingT } = useAnalyticsQuery(
    `tedarikci-${id}`,
    async ({ signal }) => {
      const all = await tryFetch(`${API}/api/tedarikciler`, signal);
      if (!Array.isArray(all)) return null;
      return all.find(t => String(t.id) === String(id)) || null;
    },
    [id],
  );

  const { data: scorecard, loading: loadingS } = useAnalyticsQuery(
    `scorecard-${id}`,
    ({ signal }) => tryFetch(`${API}/api/reports/supplier-scorecard?tedarikci_id=${id}&months=12`, signal),
    [id],
  );

  const { data: balance } = useAnalyticsQuery(
    `balance-${id}`,
    ({ signal }) => tryFetch(`${API}/api/finance/suppliers/${id}/balance`, signal),
    [id],
  );

  const { data: faturalar } = useAnalyticsQuery(
    `faturalar-${id}`,
    ({ signal }) => tryFetch(`${API}/api/faturalar?tedarikci_id=${id}&limit=20`, signal),
    [id],
  );

  const monthly = useMemo(() => {
    const months = scorecard?.aylik_hacim || scorecard?.monthly || scorecard?.months || [];
    return months.map(m => ({
      label: m.ay || m.month || m.label || '',
      hacim: Number(m.hacim || m.toplam_hacim || m.toplam || 0),
    }));
  }, [scorecard]);

  const totalHacim = useMemo(() => {
    return scorecard?.toplam_hacim ?? monthly.reduce((s, m) => s + m.hacim, 0);
  }, [scorecard, monthly]);

  const onTime = scorecard?.on_time_pct ?? null;
  const kalite = scorecard?.kalite_skoru ?? null;
  const red = scorecard?.red_orani ?? null;
  const bakiye = balance?.bakiye ?? balance?.acik_bakiye ?? null;

  const faturaRows = useMemo(() => {
    const arr = Array.isArray(faturalar) ? faturalar : (faturalar?.items || []);
    return arr.slice(0, 20);
  }, [faturalar]);

  const onPdf = () => {
    if (containerRef.current) exportPdf(containerRef.current, `tedarikci-${id}`);
  };

  const loading = loadingT || loadingS;

  return (
    <div ref={containerRef} style={{
      padding: theme.space.lg, display: 'flex', flexDirection: 'column', gap: theme.space.lg,
      background: theme.color.surfaceAlt, minHeight: '100vh', fontFamily: theme.font.family,
    }}>
      <button
        onClick={() => navigate('/analiz/tedarikci')}
        style={{
          background: 'transparent', border: 'none', cursor: 'pointer',
          color: theme.color.accent, fontSize: theme.font.size.sm,
          padding: 0, alignSelf: 'flex-start', fontFamily: theme.font.family,
        }}>← Tedarikçi listesine dön</button>

      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: theme.space.base }}>
        <div>
          <h1 style={{ margin: 0, color: theme.color.text, fontSize: theme.font.size.xxl, fontWeight: theme.font.weight.bold }}>
            {loadingT ? 'Yükleniyor...' : (tedarikci?.unvan || 'Tedarikçi bulunamadı')}
          </h1>
          {tedarikci && (
            <div style={{ color: theme.color.textSoft, fontSize: theme.font.size.sm, marginTop: 4 }}>
              Kategori: {tedarikci.kategori || '—'} · Aktif: {tedarikci.aktif ? 'Evet' : 'Hayır'}
            </div>
          )}
        </div>
        <ExportButton onExportPdf={onPdf} />
      </header>

      {!loadingT && !tedarikci && (
        <EmptyState title="Bu ID için tedarikçi bulunamadı" />
      )}

      {tedarikci && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: theme.space.base }}>
            <KpiCard title="Açık Bakiye" value={bakiye == null ? null : formatTRY(bakiye)} emptyLabel="Veri yok" />
            <KpiCard title="12 Ay Hacim" value={loading ? undefined : formatTRY(totalHacim)} loading={loading} />
            <KpiCard title="On-Time" value={onTime == null ? null : formatPct(onTime)} emptyLabel="—" />
            <KpiCard title="Kalite Skoru" value={kalite == null ? null : kalite.toFixed(1)} emptyLabel="—" />
            <KpiCard title="Red Oranı" value={red == null ? null : formatPct(red)} increaseIsBad emptyLabel="—" />
          </div>

          <ChartCard title="Aylık Hacim Trendi" subtitle="Son 12 ay" height={280}
                     loading={loadingS} empty={!loadingS && monthly.length === 0}>
            <TrendLine
              data={monthly}
              series={[{ key: 'hacim', label: 'Hacim' }]}
              yFormatter={formatCompact}
            />
          </ChartCard>

          <ChartCard title="Son Faturalar">
            {faturaRows.length === 0 ? (
              <EmptyState title="Bu tedarikçinin faturası bulunamadı" />
            ) : (
              <DataTable
                rows={faturaRows.map((f, i) => ({ id: f.id ?? i, ...f }))}
                columns={[
                  { key: 'fatura_no', label: 'Fatura No' },
                  { key: 'fatura_tarihi', label: 'Tarih' },
                  { key: 'toplam_tutar', label: 'Tutar', align: 'right', render: (v) => formatTRY(v) },
                  { key: 'vade_tarihi', label: 'Vade' },
                  { key: 'durum', label: 'Durum' },
                ]}
                initialSort={{ key: 'fatura_tarihi', dir: 'desc' }}
              />
            )}
          </ChartCard>

          <div style={{ display: 'flex', gap: theme.space.base, flexWrap: 'wrap' }}>
            <button
              onClick={() => navigate(`/fiyat-girisi?tedarikci_id=${id}`)}
              style={{
                padding: `${theme.space.sm}px ${theme.space.lg}px`,
                background: theme.color.accent, color: '#fff', border: 'none',
                borderRadius: theme.radius.sm, cursor: 'pointer',
                fontSize: theme.font.size.sm, fontWeight: theme.font.weight.medium,
                fontFamily: theme.font.family,
              }}>→ Fatura Girişi</button>
            <button
              onClick={() => navigate('/cari-hesaplar')}
              style={{
                padding: `${theme.space.sm}px ${theme.space.lg}px`,
                background: theme.color.surface, color: theme.color.text,
                border: `1px solid ${theme.color.border}`,
                borderRadius: theme.radius.sm, cursor: 'pointer',
                fontSize: theme.font.size.sm, fontWeight: theme.font.weight.medium,
                fontFamily: theme.font.family,
              }}>Cari Hesaplar</button>
          </div>
        </>
      )}
    </div>
  );
}
